/**
 * 岐阜県の料理12品。
 *
 * 飛騨と美濃で食べものがまったく違う県なので、南北から半分ずつ取っている。
 * 飛騨から赤かぶ漬け・こも豆腐・朴葉味噌・鶏ちゃん、
 * 美濃と東濃から栗きんとん・水まんじゅう・みょうがぼち・富有柿・からすみ・
 * 桑の木豆煮・五平餅・鮎の塩焼。
 *
 * 他県と重ならないように外したもの。
 *  - 干し柿 … 島根が「干し柿（西条柿）」で公開済み。生の富有柿にした
 *  - 鮎の甘露煮・小鮎の山椒煮 … 熊本と滋賀で公開済み。串に刺した塩焼にした
 *  - とち餅 … 京都で公開済み
 *
 * 鮎の塩焼は群馬の「鮎塩焼き」と名前が重なるが、そのまま残すと決めた（2026-09-23）。
 * 岐阜はまっすぐ刺した長良川の形、群馬は踊り串のジグザグで、絵は別物。
 *  - 鮒味噌 … 愛知の尾張にも同じものがある。どちらにも入れないことにした
 *  - 朴葉ずし … 朴葉味噌と葉が二重になるので、こも豆腐に替えた
 *  - みそぎだんご・いももち … 五平餅・鮎と串が三重になるので外した
 *  - そば・そうめん・ラーメン・和牛のすき焼き … すでに他県に多い
 *
 * 飛騨牛は朴葉味噌の具としてだけ出す。
 * 農水省の朴葉味噌のページに「飛騨牛やキノコ、ネギなども一緒に食べる朴葉味噌もある」
 * と書かれているので、この形なら他県のすき焼きと絵がぶつからない。
 * 飛騨牛の等級や認定条件は県公式で確認できなかったので書かない。
 *
 * 富有柿の「日本一」は書かない。
 * 岐阜県公式にあるのは「日本一かき王国づくりを進めています」という取組の表現で、
 * 生産量が日本一という意味ではない。使えるのは「富有柿発祥の地」と
 * 「栽培面積の80パーセント」、果宝柿の「350グラム以上・糖度18度以上」まで。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：赤かぶ漬け・みょうがぼち・からすみ・桑の木豆煮（店で食べられる品に差し替え）
 * - 漬物ステーキ：農林水産省「うちの郷土料理」・岐阜県・高山市の公式ページで漬物ステーキの紹介を探したが見つからず、出典は岐阜県観光連盟の公式サイト（優先順位3）にとどまる。国・自治体の資料がないので「郷土料理として認定」のような書き方はしない
 * - 高山ラーメン（飛騨高山中華そば）：発祥の店と年（昭和13年に屋台で売り出したのが始まり）は観光連盟のページに「言われ」とあるだけで、国・自治体の資料で裏が取れないので書かない。店名も書かない
 * - 高山ラーメン（飛騨高山中華そば）：具（チャーシュー・メンマ・ネギ）は店ごとの紹介にしか出てこず、高山ラーメン全体の決まりとして書いた公式ページがないので書かない
 * - 高山ラーメン（飛騨高山中華そば）：「全国にも多くのファンを持つ」は評価の言い回しなので使わない
 * - 飛騨牛にぎり：観光連盟のページにある「日本一の黒毛和牛」は、全国肉用牛枝肉共励会での受賞にもとづく称号だが、国・県の資料では確かめていないので書かない
 * - 飛騨牛にぎり：古い町並で、えびせんべいや手焼きせんべいを皿がわりにして出す食べ歩きの形は、個々の店の紹介にしか出てこないので書かない。「元祖」「発祥の店」も店の名乗りなので書かない
 * - 関のうなぎ：「蒸さずに焼く（地焼き）」「腹開き・関西風」は、店の紹介や店のサイト・個人の記事にしか見つからず、関市・観光協会・観光連盟の一般的な説明では確かめられなかったので書かない
 * - 関のうなぎ：「東海地方で一番おいしいという声もある」は評価の言い回しなので書かない
 * - 関のうなぎ：「鵜が飲み込みにくく『う』が『なんぎ』する」という名前の説は、関市のうなぎの説明ではなく語源の俗説なので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { gifuSceneryTour } from './gifuScenes';
import { GIFU_FOOD_HULLS } from './gifuFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'kuri-kinton',
    level: 1,
    name: '栗きんとん',
    shortName: '栗きんとん',
    image: '/assets/foods/gifu/01_kuri_kinton.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '布巾でしぼった跡が、筋になって残る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/38_17_gifu.html',
    icon: { label: '栗きんとん', shape: 'mochi', baseColor: '#d8bc7c', accentColor: '#a07838' },
    reward: {
      unlockScore: 200,
      area: '中津川市',
      headline: '蒸した栗を砂糖と炊き上げ、湿らせた布巾で茶巾にしぼった中津川の和菓子。',
      tip: '材料は栗と砂糖だけ。練ったものを棒状に伸ばして約30グラムの輪切りにし、丸く整えてから布巾で包んでしぼる。中津川は栗の産地で、江戸時代の宿場町として茶の湯文化が栄え、茶に合う菓子を菓子職人が競ったことから生まれたとされる。九月九日は重陽の節句、別名を栗節句という。',
      links: searchLinks('中津川市', '栗きんとん'),
    },
  },
  {
    id: 'mizu-manju',
    level: 2,
    name: '水まんじゅう',
    shortName: '水まんじゅう',
    image: '/assets/foods/gifu/02_mizu_manju.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '向こうが透けて、中のあんが黒く見える。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/38_18_gifu.html',
    icon: { label: '水まんじゅう', shape: 'mochi', baseColor: '#e8f0f0', accentColor: '#3a2820' },
    reward: {
      unlockScore: 500,
      area: '大垣市',
      headline: 'くず粉とわらび粉の生地であんを包み、井戸水で冷やした水の都・大垣の夏菓子。',
      tip: '誕生は明治時代といわれる。地下水で野菜や果物を冷やす「井戸舟」が各家庭にあり、それを活かして夏の冷たい菓子として作られた。おちょこに生地とあん玉を詰めて透明になるまで約十分蒸し、井戸水で冷やす。菓子店の店頭で水槽に冷やされる姿が、大垣に夏の訪れを告げる風物詩になっている。',
      links: searchLinks('大垣市', '水まんじゅう'),
    },
  },
  {
    id: 'gohei-mochi',
    level: 3,
    name: '五平餅',
    shortName: '五平餅',
    image: '/assets/foods/gifu/03_gohei_mochi.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'わらじの形に整えて、串を通す。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/38_8_gifu.html',
    icon: { label: '五平餅', shape: 'dumpling', baseColor: '#c89858', accentColor: '#6a4020' },
    reward: {
      unlockScore: 1000,
      area: '中津川市・恵那市',
      headline: 'つぶした米を串に巻きつけてわらじ形に整え、くるみと落花生のたれを塗って焼いた東濃の餅。',
      tip: '江戸時代中期、木曽地域の木こりが木の切れ端に飯を握りつけてたき火で焼き、味噌をつけて食べたのが始まりといわれる。神に捧げる御幣に形が似ていることから名がついたとされる。形はわらじ形・平たい団子形・丸い団子形など地域や家庭でさまざま。たれは中山道を境に北が醤油味、南が味噌味が多い。',
      links: searchLinks('中津川市', '五平餅'),
    },
  },
  {
    id: 'tsukemono-steak',
    level: 4,
    name: '漬物ステーキ',
    shortName: '漬物ステーキ',
    image: '/assets/foods/gifu/04_tsukemono_steak.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '白菜や赤かぶなどの切り漬けを油でよく炒め、醤油をかけて溶き卵でとじる。器や鉄板に盛り、好みで鰹節や七味をかける。飛騨地方に古くから伝わる料理で、「漬けステ」とも呼ばれる。',
    source:
      'https://www.kankou-gifu.jp/blog/detail_13.html',
    icon: { label: '漬物', shape: 'curry-plate', baseColor: '#d9b85c', accentColor: '#f2d34a' },
    reward: {
      unlockScore: 1800,
      area: '高山市ほか飛騨地方',
      headline: '凍った漬物を焼いて食べた',
      tip: '冬の寒さで凍ってしまった漬物を、朴葉にのせて囲炉裏端で温めて食べたのが始まりとされる。酒のつまみにも、ご飯のおかずにもなる。',
      links: searchLinks('高山市', '漬物ステーキ'),
      official: [{ label: '岐阜の旅ガイド「漬物ステーキ」', url: 'https://www.kankou-gifu.jp/blog/detail_13.html' }],
    },
  },
  {
    id: 'fuyu-gaki',
    level: 5,
    name: '富有柿',
    shortName: '富有柿',
    image: '/assets/foods/gifu/05_fuyu_gaki.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '岐阜県が発祥の地とされる、大きくて紅い実。',
    source:
      'https://www.pref.gifu.lg.jp/page/1357.html',
    icon: { label: '富有柿', shape: 'berry', baseColor: '#d86828', accentColor: '#5a7a30' },
    reward: {
      unlockScore: 3000,
      area: '本巣市・瑞穂市',
      headline: '岐阜県が発祥の地とされ、県内の柿の栽培面積の八割を占める十一月収穫の甘柿。',
      tip: '岐阜県は富有柿発祥の地で、富有は岐阜県で選抜されてできた品種。十一月収穫の富有が県内の栽培面積の80パーセントを占める。袋掛けした富有柿の最高級品は果宝柿と呼ばれ、350グラム以上の大きさ、鮮やかな紅色、糖度18度以上という基準で選ばれる。',
      links: searchLinks('本巣市', '富有柿'),
    },
  },
  {
    id: 'takayama-ramen',
    level: 6,
    name: '高山ラーメン（飛騨高山中華そば）',
    shortName: '高山ラーメン',
    image: '/assets/foods/gifu/06_takayama_ramen.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '和風だしの醤油スープに、細めの縮れ麺を入れる。だしは店ごとに鶏ガラ・魚介・野菜・椎茸などを使い分ける。地元では「中華そば」や「そば」と呼ばれ、日常的に食べられてきた。',
    source:
      'https://www.kankou-gifu.jp/blog/detail_224.html',
    icon: { label: '中華そば', shape: 'ramen', baseColor: '#a8743a', accentColor: '#6b8e3a' },
    reward: {
      unlockScore: 4500,
      area: '高山市',
      headline: '高山で「そば」といえばこれ',
      tip: '年越しそばとして中華そばを食べる家庭もある。高山ラーメンのほか、飛騨高山ラーメン、飛騨ラーメンとも呼ばれる。',
      links: searchLinks('高山市', '高山ラーメン'),
      official: [{ label: '岐阜の旅ガイド「高山ラーメン」', url: 'https://www.kankou-gifu.jp/gourmet/detail_6363.html' }],
    },
  },
  {
    id: 'komo-dofu',
    level: 7,
    name: 'こも豆腐',
    shortName: 'こも豆腐',
    image: '/assets/foods/gifu/07_komo_dofu.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '藁の縞が、そのまま模様になる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/38_29_gifu.html',
    icon: { label: 'こも豆腐', shape: 'mochi', baseColor: '#e8dcc0', accentColor: '#a08048' },
    reward: {
      unlockScore: 6500,
      area: '飛騨市・高山市',
      headline: '豆腐を藁で編んだ「こも」で巻いて一時間茹で、だしで煮含めた飛騨のもてなし料理。',
      tip: 'スが立つことでできる無数の気泡と、豆腐の表面にしみ込んだ藁の香りと模様が特徴。気泡があるので出汁で煮込むと味がしみ込みやすく、型崩れもしない。豆腐を縦に四つに切り、二本ずつこもで巻いてたっぷりの湯で一時間ほど茹でる。正月のおせちや雑煮に入るほか、祝い事でも仏事でも供される。',
      links: searchLinks('飛騨市', 'こも豆腐'),
    },
  },
  {
    id: 'ayu-shioyaki',
    level: 8,
    name: '鮎の塩焼',
    shortName: '鮎の塩焼',
    image: '/assets/foods/gifu/08_ayu_shioyaki.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '尾とヒレだけ、塩が白く残る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/38_30_gifu.html',
    icon: { label: '鮎の塩焼', shape: 'fish', baseColor: '#9aa8a0', accentColor: '#f0ece0' },
    reward: {
      unlockScore: 9000,
      area: '岐阜市・郡上市',
      headline: '縦に串を打ち、ヒレと尾に化粧塩をして炭火でじっくり焼いた長良川の鮎。',
      tip: '鮎全体に軽く塩をふって五、六分おき、そのあとヒレと尾に化粧塩をして両面を焼く。伝承地域は木曽川流域の中濃、長良川流域、揖斐川流域の西濃。長良川には千三百年の歴史をもつ鵜飼があり、獲れた鮎は皇室や伊勢神宮へ奉納されている。七月から八月が適期で、八月以降は卵を抱えた子持ち鮎も味わえる。',
      links: searchLinks('岐阜市', '鮎の塩焼'),
    },
  },
  {
    id: 'hoba-miso',
    level: 9,
    name: '朴葉味噌',
    shortName: '朴葉味噌',
    image: '/assets/foods/gifu/09_hoba_miso.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '葉が皿になり、そのまま火にかかる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/38_10_gifu.html',
    icon: { label: '朴葉味噌', shape: 'curry-plate', baseColor: '#8a6a40', accentColor: '#7a2a20' },
    reward: {
      unlockScore: 12000,
      area: '高山市',
      headline: '茶色い朴の枯れ葉を皿にして味噌をのせ、ねぎとしいたけと飛騨牛を一緒に焼く飛騨の一品。',
      tip: '林業が盛んだった飛騨で、山仕事をする人が朴葉を皿代わりに焼き味噌をしたのが始まりとされる。秋に落葉した朴の葉を冬になる前に集めて保存して使う。囲炉裏の火に朴の葉を敷き、漬物や味噌を温めながら食べた。今は味噌だけでなく、飛騨牛やキノコ、ネギも一緒に焼く朴葉味噌もある。',
      links: searchLinks('高山市', '朴葉味噌'),
    },
  },
  {
    id: 'keichan',
    level: 10,
    name: '鶏ちゃん',
    shortName: '鶏ちゃん',
    image: '/assets/foods/gifu/10_keichan.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '鉄鍋の上で、味噌が焦げていく。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/38_2_gifu.html',
    icon: { label: '鶏ちゃん', shape: 'hotpot', baseColor: '#8a4a28', accentColor: '#7aa03a' },
    reward: {
      unlockScore: 16000,
      area: '郡上市・下呂市',
      headline: '味噌だれをからめた鶏肉とキャベツを、中央が盛り上がった鉄鍋で焼きながら食べる郷土の鍋。',
      tip: '誕生は1950年ごろで、ジンギスカンをまねて作られたのが始まりとされる。1960年ごろから地元の精肉店や居酒屋が独自に改良した。各家庭で飼っていたニワトリが卵を産まなくなったあと、貴重なたんぱく源として食べたのが起源といわれる。味噌味・醤油味・塩味など地域や家庭で異なる。',
      links: searchLinks('郡上市', '鶏ちゃん'),
    },
  },
  {
    id: 'hidagyu-nigiri',
    level: 11,
    name: '飛騨牛にぎり',
    shortName: '飛騨牛にぎり',
    image: '/assets/foods/gifu/11_hidagyu_nigiri.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: 'やわらかな霜降りが特徴の飛騨牛を、しゃりにのせて握る。生のまま、またはあぶって食べる。飛騨牛は、肉質が5段階の3以上など一定の基準を満たした和牛だけが名乗れる。',
    source:
      'https://www.kankou-gifu.jp/article/gifu-hidagyu/top/',
    icon: { label: '牛にぎり', shape: 'sashimi-platter', baseColor: '#e8a6a6', accentColor: '#f5f0e6' },
    reward: {
      unlockScore: 22000,
      area: '高山市',
      headline: '霜降りの飛騨牛を握る',
      tip: '飛騨牛を名乗るには、岐阜県が主な飼養地で、飛騨牛銘柄推進協議会に登録した農家が14か月以上肥育した黒毛和種であることも条件になる。',
      links: searchLinks('高山市', '飛騨牛にぎり寿司'),
      official: [{ label: 'ぎふ地産地消めぐり（岐阜県農政部）「飛騨牛」', url: 'https://gifu-tisan.jp/farmgoods/hidagyu/' }],
    },
  },
  {
    id: 'seki-unagi',
    level: 12,
    name: '関のうなぎ',
    shortName: '関のうなぎ',
    image: '/assets/foods/gifu/12_seki_unagi.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '秘伝のたれを絡め、ふっくら香ばしく焼き上げたうなぎ。刃物のまちとして知られる関市は、うなぎの名店が多く並ぶ地域でもある。古くから刀匠や鵜匠のスタミナ源として重宝されてきたという。',
    source:
      'https://sekikanko.jp/feature/hajimete',
    icon: { label: 'うなぎ', shape: 'fish', baseColor: '#8a4b1f', accentColor: '#3a2410' },
    reward: {
      unlockScore: 30000,
      area: '関市',
      headline: '刃物のまちのうなぎ',
      tip: '鎌倉時代に関へ移り住んだ刀匠たちのスタミナ源として、うなぎやうな丼が重宝されたと伝わる。市内にはうな丼を出す店がたくさんある。',
      links: searchLinks('関市', 'うなぎ'),
      official: [{ label: '岐阜の旅ガイド「関うな丼」', url: 'https://www.kankou-gifu.jp/gourmet/detail_3523.html' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const gifuFoodStage: StageDefinition = {
  regionId: 'gifu',
  regionName: '岐阜県',
  mode: 'food',
  title: '岐阜めしマージ',
  theme: {
    primaryColor: '#b04828',
    secondaryColor: '#c8a860',
    accentColor: '#e8d090',
    backgroundColor: '#141210',
  },
  /** 背景ツアー「岐阜めぐり」。軸は一日の時間。朝の天空の茶畑から、夜の長良川鵜飼へ。 */
  scenery: {
    tour: gifuSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, GIFU_FOOD_HULLS),
};
